import { useCallback, useEffect, useState } from "react";
import {
  copyNumber,
  copyText,
  waplifyChatHref,
  type CrmStage,
} from "../Worklist/api";
import type { Severity } from "../Worklist/priority";
import { ghostBtn, microLabel, pillTone, primaryBtn } from "../Worklist/ui";
import type { RetentionItem } from "./pipeline";

/**
 * A single subscription in the renewal pipeline. Grammar mirrors the worklist's
 * CareWaitingCard (severity rail + wash, tabular scan anchor, copyable draft block,
 * actions pinned right) but the content and actions are retention-specific.
 *
 * DRAFT-FIRST, and it must be UNMISTAKABLE: nothing here sends a message. "Open in
 * waplify" is an <a> into the Care Manager chat where a HUMAN types and sends; "Copy
 * draft" copies text; the outcome buttons only RECORD a pipeline stage (picard
 * crm-stage/), which messages nobody.
 *
 * Colour tokens duplicated from the worklist deliberately (Horizon has no `slate`,
 * `red-50` is a 10%-alpha wash) — a 3-line map reads cheaper than a shared abstraction.
 */
const rail: Record<Severity, string> = {
  critical: "border-l-4 ![border-left-color:#dc2626]",
  warning: "border-l-4 ![border-left-color:#f59e0b]",
  muted:
    "border-l-4 ![border-left-color:#cbd5e1] dark:![border-left-color:#334155]",
};

const wash: Record<Severity, string> = {
  critical: "bg-crit-50 hover:bg-crit-100 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  warning: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  muted: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
};

const urgencyTone: Record<Severity, string> = {
  critical: "text-crit-600 dark:text-red-400",
  warning: "text-warn-600 dark:text-amber-400",
  muted: "text-clinic-500 dark:text-gray-400",
};

// scan anchor is w-14 (56px) + gap-4 (16px) — indent the content-column blocks to align
// under the identity, not under the anchor.
const CONTENT_INDENT = "ml-[72px]";

/** The outcome buttons — each RECORDS a stage; the terminal three close the task. */
const OUTCOMES: ReadonlyArray<{ stage: CrmStage; label: string; terminal: boolean }> = [
  { stage: "renewed", label: "Renewed", terminal: true },
  { stage: "not_interested", label: "Not interested", terminal: true },
  { stage: "no_response", label: "No response", terminal: true },
  { stage: "call_back", label: "Call back", terminal: false },
];

interface Props {
  item: RetentionItem;
  /**
   * Record a pipeline stage for this customer. This is the ONLY mutation the card makes
   * and it messages NOBODY — it just moves the card between stages (or drops it, for a
   * terminal outcome). The actual renewal message is sent by a human in waplify.
   */
  onStage: (item: RetentionItem, stage: CrmStage) => void;
  pending: boolean;
  error: string | null;
}

export default function RetentionCard({ item, onStage, pending, error }: Props) {
  const [copiedDraft, setCopiedDraft] = useState(false);
  const [copiedNum, setCopiedNum] = useState(false);

  const waHref = waplifyChatHref(item.mobile);

  useEffect(() => {
    if (!copiedDraft) return undefined;
    const t = window.setTimeout(() => setCopiedDraft(false), 2000);
    return () => window.clearTimeout(t);
  }, [copiedDraft]);

  useEffect(() => {
    if (!copiedNum) return undefined;
    const t = window.setTimeout(() => setCopiedNum(false), 2000);
    return () => window.clearTimeout(t);
  }, [copiedNum]);

  // The ?phone= deep-link may not be live on every waplify build — copy the number too
  // so a CM can paste it into waplify's search. Fire-and-forget; the link opens anyway.
  const onOpenWaplify = useCallback(() => {
    void copyNumber(item.mobile).then(setCopiedNum);
  }, [item.mobile]);

  const onCopyDraft = useCallback(() => {
    void copyText(item.draft).then(setCopiedDraft);
  }, [item.draft]);

  const alreadyContacted = item.stage !== "at_risk";

  return (
    <li
      data-testid="retention-card"
      data-stage={item.stage}
      className={`relative flex flex-col gap-2 px-4 py-2.5 transition-colors first:rounded-t-[7px] last:rounded-b-[7px] motion-reduce:transition-none ${rail[item.severity]} ${wash[item.severity]}`}
    >
      <div className="flex items-start gap-4">
        {/* scan anchor — one tabular number, so a screenful ranks at a glance */}
        <div className="w-14 shrink-0 pt-0.5 text-center">
          <div
            className={`text-xl font-bold tabular-nums leading-none ${urgencyTone[item.severity]}`}
          >
            {item.urgencyValue}
          </div>
          <div className="mt-1 font-mono text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] text-clinic-500 dark:text-gray-400">
            {item.urgencyLabel}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[15px] font-semibold leading-tight text-clinic-900 dark:text-white">
              {item.name}
            </span>
            <span className="text-xs tabular-nums text-clinic-500 dark:text-gray-400">
              {item.mobile}
            </span>
            <span
              data-testid="retention-plan"
              className="rounded bg-clinic-100 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-clinic-600 ring-1 ring-clinic-200 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600"
            >
              {item.plan}
            </span>
            <span
              data-testid="retention-expiry"
              className={`rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${pillTone[item.severity]}`}
            >
              {item.expiryLabel}
            </span>
          </div>

          <p className="mt-1 text-[13px] leading-snug text-clinic-700 dark:text-gray-200">
            {item.meta}
          </p>
          {item.note && (
            <p className="mt-0.5 text-xs italic leading-snug text-clinic-500 dark:text-gray-400">
              Note: {item.note}
              {item.updatedBy ? ` — ${item.updatedBy}` : ""}
            </p>
          )}

          {error && (
            <p
              role="alert"
              data-testid="retention-card-error"
              className="mt-1.5 rounded bg-crit-100 px-2 py-1 text-xs font-medium text-crit-700 ring-1 ring-crit-200"
            >
              {error} — the card is unchanged. Retry, or reload the pipeline.
            </p>
          )}
        </div>

        {/* the human goes HERE to send — a link into waplify, never an auto-send */}
        <div className="flex shrink-0 items-center gap-1.5">
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="retention-open-waplify"
              onClick={onOpenWaplify}
              className={primaryBtn}
            >
              Open in waplify
            </a>
          )}
          {!alreadyContacted && (
            <button
              type="button"
              data-testid="retention-mark-contacted"
              disabled={pending}
              onClick={() => onStage(item, "contacted")}
              className={ghostBtn}
            >
              {pending ? "Working…" : "Mark contacted"}
            </button>
          )}
        </div>
      </div>

      {/*
        The renewal draft — COPY, never send. Personalised client-side. The label makes
        the draft-first rule explicit: a human copies it and sends it in waplify.
      */}
      <div
        data-testid="retention-draft"
        className={`${CONTENT_INDENT} rounded-md border border-clinic-200 bg-clinic-50 px-2.5 py-2 dark:border-navy-700 dark:!bg-navy-900`}
      >
        <div className="flex items-center justify-between gap-3">
          <span className={microLabel}>
            Renewal draft — copy &amp; send in waplify (nothing auto-sends)
          </span>
          <button
            type="button"
            data-testid="retention-copy-draft"
            onClick={onCopyDraft}
            className={ghostBtn}
          >
            {copiedDraft ? "Copied ✓" : "Copy draft"}
          </button>
        </div>
        <p className="mt-1.5 text-[13px] leading-snug text-clinic-700 dark:text-gray-200">
          {item.draft}
        </p>
      </div>

      {/*
        Outcome capture — these RECORD where the customer landed; they message no one.
        The terminal three close the task (the card drops out); "Call back" parks it in
        the Call back stage. The label spells out that recording ≠ sending.
      */}
      <div
        role="group"
        aria-label="Record outcome — no message is sent"
        className={`${CONTENT_INDENT} flex flex-wrap items-center gap-1.5`}
      >
        <span className={`${microLabel} mr-1`}>Record outcome</span>
        {OUTCOMES.map((o) => (
          <button
            key={o.stage}
            type="button"
            data-testid={`retention-outcome-${o.stage}`}
            disabled={pending}
            onClick={() => onStage(item, o.stage)}
            className={`touch-manipulation rounded-md px-2.5 py-1 text-xs font-medium ring-1 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-navy-800 ${
              o.terminal
                ? "bg-white text-clinic-700 ring-clinic-300 hover:bg-clinic-100 hover:text-clinic-900 dark:!bg-navy-800 dark:text-gray-200 dark:ring-navy-600 dark:hover:bg-navy-700"
                : "bg-warn-50 text-warn-700 ring-warn-200 hover:bg-warn-100 dark:!bg-navy-900 dark:text-amber-200 dark:ring-navy-700"
            }`}
          >
            {o.label}
          </button>
        ))}
        <span className="ml-auto text-[10px] font-medium text-clinic-400 dark:text-gray-500">
          Records status only · no message sent
        </span>
      </div>

      {/* Announced, not just shown — the copy is what makes the deep-link usable today. */}
      <span aria-live="polite" className="sr-only">
        {copiedNum ? "Number copied to clipboard" : ""}
      </span>
      {copiedNum && (
        <span
          data-testid="retention-num-copied"
          className="pointer-events-none absolute right-3 top-1 rounded bg-clinic-900 px-2 py-0.5 text-[10px] font-medium text-white shadow"
        >
          Number copied
        </span>
      )}
    </li>
  );
}
